import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Send, Bot, User, Sparkles, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AIAssistantPanel = ({ open, onOpenChange }) => {
  const { api, user, isDirector } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hello ${user?.first_name}! I'm your ICRCL AI Assistant. I can help you with:\n\n• Drafting progress notes and reports\n• Answering clinical procedure questions\n• Guidance on therapy approaches for children with SEND\n• Explaining policies and procedures\n\nHow can I assist you today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('helper');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', {
        message: input,
        mode: mode,
        context: { user_role: user?.role, discipline: user?.discipline }
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get AI response';
      toast.error(errorMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again or contact support if the issue persists.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#2a9d8f] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <SheetTitle className="text-[#1e3a5f]">AI Assistant</SheetTitle>
                <p className="text-xs text-slate-500">Powered by GPT-5.2</p>
              </div>
            </div>
            {isDirector && (
              <div className="flex gap-2">
                <Badge 
                  variant={mode === 'helper' ? 'default' : 'outline'}
                  className={`cursor-pointer ${mode === 'helper' ? 'bg-[#2a9d8f]' : ''}`}
                  onClick={() => setMode('helper')}
                >
                  Helper
                </Badge>
                <Badge 
                  variant={mode === 'analytic' ? 'default' : 'outline'}
                  className={`cursor-pointer ${mode === 'analytic' ? 'bg-[#1e3a5f]' : ''}`}
                  onClick={() => setMode('analytic')}
                >
                  Analytic
                </Badge>
              </div>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
              >
                {msg.role === 'assistant' && (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.error ? 'bg-red-100' : 'bg-[#2a9d8f]/10'}`}>
                    {msg.error ? (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Bot className="w-4 h-4 text-[#2a9d8f]" />
                    )}
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#1e3a5f] text-white'
                      : msg.error
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-slate-100 text-slate-800'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#2a9d8f]" />
                </div>
                <div className="bg-slate-100 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-slate-100">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about clinical procedures, reports, or therapy guidance..."
              className="resize-none min-h-[60px]"
              data-testid="ai-chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 h-auto"
              data-testid="ai-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            AI responses are for guidance only. Always verify clinical decisions.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AIAssistantPanel;
